/**
 * Restart-flag IPC between channel MCP subprocesses (e.g. telegram-channel)
 * and the manager. A channel handler that observes a `/restart` command for
 * its agent writes a flag file here; the manager poll loop scans this dir
 * each cycle, kills the agent's tmux session (which lets the existing
 * "ensure session" path respawn it), and posts an ack back to the
 * originating channel.
 *
 * Filename: `<codeName>.flag` so concurrent /restart commands collapse to
 * one (idempotent — repeated taps don't queue multiple restarts).
 *
 * Body: JSON record describing where the request came from so the manager
 * can ack to the right place.
 */

import { existsSync, mkdirSync, readdirSync, readFileSync, renameSync, rmSync, writeFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { randomUUID } from 'node:crypto';

export interface RestartFlag {
  /** Agent code_name. Matches the filename stem. */
  codeName: string;
  /** Channel source for the ack reply, e.g. "telegram". */
  source: string;
  /** Unix epoch ms when the flag was written. */
  ts: number;
  /** Channel-specific routing for the ack reply (chat id, thread id, etc). */
  reply?: Record<string, string | number>;
  /**
   * ENG-6229: free-text describing why the agent asked to restart (the change
   * it needs to reload). Only set by the `source: 'agent'` self-restart path;
   * the manager surfaces it in the restart log. The breaker reason is derived
   * from `source`, not from this field.
   */
  reason?: string;
}

export function restartFlagsDir(): string {
  return join(homedir(), '.augmented', 'restart-flags');
}

function flagPath(codeName: string): string {
  return join(restartFlagsDir(), `${codeName}.flag`);
}

export function writeRestartFlag(flag: RestartFlag): string {
  const dir = restartFlagsDir();
  if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
  const path = flagPath(flag.codeName);
  // Atomic write: write to a sibling temp path then rename. Without this,
  // a manager poll that fires during the writeFileSync window would read a
  // partial flag and fall into the corrupt-file fallback — losing source +
  // reply routing, which means the restart fires but the operator never gets
  // the final ack.
  // Include a per-call randomUUID so concurrent writes (overlapping channel
  // handlers, retries) can't collide on the same tmp path and have one
  // renameSync remove the file the other is about to rename.
  const tmpPath = `${path}.${process.pid}.${randomUUID()}.tmp`;
  writeFileSync(tmpPath, JSON.stringify(flag) + '\n', 'utf8');
  renameSync(tmpPath, path);
  return path;
}

export function readRestartFlags(): RestartFlag[] {
  const dir = restartFlagsDir();
  if (!existsSync(dir)) return [];
  const out: RestartFlag[] = [];
  for (const entry of readdirSync(dir)) {
    if (!entry.endsWith('.flag')) continue;
    try {
      const raw = readFileSync(join(dir, entry), 'utf8');
      const parsed = JSON.parse(raw) as RestartFlag;
      // Defensive: derive codeName from filename if the body is malformed,
      // so a stray flag file always identifies an agent.
      if (typeof parsed.codeName !== 'string' || parsed.codeName.length === 0) {
        parsed.codeName = entry.replace(/\.flag$/, '');
      }
      out.push(parsed);
    } catch {
      // Corrupt flag — treat as a bare restart request for the agent in
      // the filename. The manager will still kick the session.
      out.push({
        codeName: entry.replace(/\.flag$/, ''),
        source: 'unknown',
        ts: Date.now(),
      });
    }
  }
  return out;
}

export function deleteRestartFlag(codeName: string): void {
  const path = flagPath(codeName);
  if (existsSync(path)) {
    rmSync(path, { force: true });
  }
}
